import {
  LayoutDashboard, LayoutGrid, Users, Ticket, Clapperboard, Film, Send, Lock,
  DollarSign, CreditCard, Wallet, Landmark, BarChart3, CheckSquare, Calendar,
  BookOpen, Bot, Briefcase, Settings, MessageSquare, Megaphone, UploadCloud,
  GraduationCap, CalendarDays, Link2, BadgeDollarSign, Zap, Smartphone, Scale,
  FileSignature, Receipt,
} from 'lucide-react';

// ─── Hub-and-spoke module registry ──────────────────────────────────────────
// Single source of truth for the "rooms". Each module owns: its identity
// (name/icon/accent), the nav items that live inside it (scoped sidebar), a
// role-visibility predicate, and an optional glanceable stat for the home card.
//
// A module's `nav[].id` matches the `currentPage` a page passes to MainLayout,
// which is how the scoped Sidebar and the active-room highlight are derived.
//
// Stats: `stat.endpoint`/`stat.pick` pull real data; `stat.todo` marks a value
// that isn't wired to real data yet (shown as a clear stub, never a fake number).

const isCreator = (u) => u?.role === 'creator';
const isStaff = (u) => !!u && u.role !== 'creator';
const canManageUsers = (u) => u?.role === 'admin' || u?.permissions?.includes('manage_users');
const isChatter = (u) => u?.role === 'chatter';
// Chatters get a deliberately small hub: their own numbers (Overview), their pay
// and payouts (Finance), their shifts and tasks (Operations), and the playbooks
// they've been given (SOP Library). Everything else — the roster, content,
// posting, agents, admin — is not theirs to see.
const isInternalStaff = (u) => isStaff(u) && !isChatter(u);
const isMarketer = (u) => u?.role === 'marketer';
// A marketer's job is content and distribution: the vault, the plan, the posting
// queue, the tasks that move them and the playbooks behind them. People stays
// removed — a marketer has no `manage_users`, so everything past the roster list
// refuses them and the room would contain one read-only table.
//
// Finance is theirs again, and deliberately: a marketer is now paid per sub
// their tracking link brings in and a percentage of the creators assigned to
// them, both accruing into the same earnings ledger a chatter's pay does. They
// have a balance, so they need the page that shows it and the wallet it is paid
// into. Agency revenue, payroll and reports stay out — those are still
// view_all_earnings, which they do not hold.
const isRosterStaff = (u) => isInternalStaff(u) && !isMarketer(u);

export const MODULES = [
  {
    id: 'overview',
    name: 'Overview',
    icon: LayoutDashboard,
    accent: 'color-mix(in srgb, var(--brand-2) 0%, var(--brand-1))',
    tagline: (u) => (isChatter(u) ? 'Your sales, shifts & commission'
      : isCreator(u) ? 'Your earnings at a glance'
      : isMarketer(u) ? 'Your subs, commission & balance'
      : 'Agency metrics at a glance'),
    landing: (u) => (isCreator(u) ? '/creator-dashboard' : '/dashboard'),
    visible: () => true,
    nav: (u) => [
      { id: 'dashboard', label: isStaff(u) && !isChatter(u) ? 'Dashboard' : 'My Dashboard', path: isCreator(u) ? '/creator-dashboard' : '/dashboard', icon: LayoutDashboard },
    ],
    // Agency net revenue today for staff; a chatter's own commission instead —
    // they have no access to agency-wide revenue and shouldn't see a dead stat.
    // Same reasoning for a marketer, whose dashboard is the posting side of the
    // agency rather than its money.
    stat: (u) => (isChatter(u)
      ? {
          endpoint: '/api/dashboard/chatter',
          pick: (d) => `$${Math.round(d?.stats?.earningsTotal || 0).toLocaleString()}`,
          suffix: 'earned',
        }
      : isMarketer(u)
      ? {
          endpoint: '/api/dashboard/marketer',
          pick: (d) => d?.stats?.subsThisMonth,
          suffix: 'subs this month',
        }
      : {
          endpoint: () => { const d = new Date().toISOString().slice(0, 10); return `/api/revenue?from=${d}&to=${d}`; },
          pick: (d) => { const n = d?.totals?.net; return n == null ? null : `$${Math.round(n).toLocaleString()}`; },
          suffix: 'today',
          fallback: 'not connected',
        }),
  },
  {
    // Creators and staff are one room: both are "people we manage", and merging
    // them frees a card on the hub for Messages without adding an eleventh room.
    id: 'people',
    name: 'People',
    tagline: 'Creators, staff & access',
    icon: Users,
    accent: 'color-mix(in srgb, var(--brand-2) 12%, var(--brand-1))',
    landing: () => '/creators',
    // Not marketers: everything past the roster list — the team, the invite
    // codes, a creator's revenue — is manage_users-gated, which leaves them a
    // room containing one read-only table.
    visible: isRosterStaff,
    nav: (u) => [
      { id: 'creators', label: 'Creators', path: '/creators', icon: Users, description: 'The roster, and their Infloww connections' },
      ...(canManageUsers(u) ? [
        { id: 'team', label: 'Team', path: '/team', icon: Briefcase, description: 'Staff, their pay and what they can see' },
        { id: 'invites', label: 'Invite Codes', path: '/invites', icon: Ticket, description: 'Codes that create an account with a role' },
      ] : []),
    ],
    stat: { endpoint: '/api/creators', pick: (d) => (d.creators || []).length, suffix: 'creators' },
  },
  {
    id: 'content',
    name: 'Content',
    tagline: (u) => (isCreator(u) ? 'Your content & uploads' : 'Assets & video production'),
    icon: Clapperboard,
    accent: 'color-mix(in srgb, var(--brand-2) 25%, var(--brand-1))',
    // A creator opens Content on her plan, not her archive. The plan is what she
    // is being asked to do this week; the library is where finished work lands
    // and is one tap away in the sidebar. Staff still land on the vault — they
    // come here to administer files, and their plan view is per-creator.
    landing: (u) => (isCreator(u) ? '/content' : '/content'),
    // Creators get in too, but only to their own library — the vault API pins
    // every read to their creator_id. Video Studio stays staff-only.
    visible: (u) => isInternalStaff(u) || isCreator(u),
    // Uploading is not a nav item for a creator: it is a control on the library
    // and on a plan idea, both of which already know where the file is going,
    // rather than a page that asks the same questions again.
    nav: (u) => (isCreator(u)
      ? [
          { id: 'content', label: 'Content Plan', path: '/content', icon: CalendarDays, description: 'What you have been asked to make this week' },
          { id: 'content-library', label: 'My Library', path: '/content/library', icon: Clapperboard, description: 'Everything you have uploaded' },
        ]
      : [
          { id: 'content', label: 'Content Vault', path: '/content', icon: Clapperboard, description: 'Every creator’s library, folders and files' },
          { id: 'content-plan', label: 'Content Plan', path: '/content/plan', icon: CalendarDays, description: 'Build a creator’s week of ideas' },
          { id: 'video-studio', label: 'Video Studio', path: '/video-studio', icon: Film, description: 'Spoof videos into unique variants' },
          { id: 'content-upload', label: 'Upload Agent', path: '/agents/content-upload', icon: UploadCloud, description: 'Guided bulk upload into the right folder' },
        ]),
    stat: { endpoint: '/api/vault/stats', pick: (d) => d.totalFiles, suffix: 'files' },
  },
  {
    id: 'posting',
    name: 'Posting',
    tagline: 'Publish & schedule',
    icon: Send,
    accent: 'color-mix(in srgb, var(--brand-2) 37%, var(--brand-1))',
    landing: () => '/posting-library',
    visible: isInternalStaff,
    nav: () => [
      { id: 'posting-library', label: 'Posting Library', path: '/posting-library', icon: Send, description: 'Posts, reminders and account stats' },
      { id: 'accounts-library', label: 'Accounts Library', path: '/accounts-library', icon: Lock, description: 'Logins and credentials, per platform' },
      { id: 'marketing-analytics', label: 'Marketing Analytics', path: '/marketing-analytics', icon: BarChart3, description: 'Views, upvotes and comments across every post' },
    ],
    stat: { endpoint: '/api/posts/stats', pick: (d) => d.postsLast7Days, suffix: 'posts / 7d' },
  },
  {
    id: 'finance',
    name: 'Finance',
    tagline: (u) => (isCreator(u) || isChatter(u) ? 'Your earnings & payouts' : 'Revenue, payroll & payouts'),
    icon: DollarSign,
    accent: 'color-mix(in srgb, var(--brand-2) 50%, var(--brand-1))',
    // Must match the first nav item for each role, or clicking the room card
    // lands somewhere the sidebar isn't offering — for a creator that meant the
    // chatter commission page, which 403s for them.
    landing: (u) => (isCreator(u) ? '/revenue/creators/me'
      : isChatter(u) || isMarketer(u) ? '/payroll/my-earnings'
      : '/revenue'),
    visible: (u) => !!u,
    // Creators and chatters see only their own earnings page — agency revenue,
    // payroll and reports are staff-only and server-gated as well.
    nav: (u) => [
      // Both see "My Earnings", but they are different things and different
      // endpoints: a chatter's is commission on attributed sales, a creator's is
      // their own revenue. Pointing creators at the chatter page gave them a 403
      // (it needs view_commission, which creators don't have) on a link the
      // sidebar was offering them. `me` resolves server-side to their profile.
      ...(isCreator(u)
        ? [
            { id: 'my-earnings', label: 'My Earnings', path: '/revenue/creators/me', icon: Wallet },
            { id: 'sales', label: 'Live Sales', path: '/sales', icon: Zap, description: 'Your sales as they land' },
          ]
        // A marketer's earnings page is the chatter one: both are paid out of
        // earnings_ledger and settle through the same withdrawal queue, so it is
        // literally the same ledger rendered, not a lookalike.
        : isChatter(u)
        ? [{ id: 'my-earnings', label: 'My Earnings', path: '/payroll/my-earnings', icon: Wallet, description: 'Your balance, and what made it up' }]
        // A marketer gets their links alongside their balance: the links are
        // what the balance is made of, and "which are mine" was previously only
        // answerable by an admin opening their team page.
        : isMarketer(u)
        ? [
            { id: 'my-earnings', label: 'My Earnings', path: '/payroll/my-earnings', icon: Wallet, description: 'Your balance, and what made it up' },
            { id: 'tracking-links', label: 'My Links', path: '/tracking-links', icon: Link2, description: 'The links credited to you, and what they brought in' },
            { id: 'sales', label: 'Live Sales', path: '/sales', icon: Zap, description: 'Sales for the creators assigned to you, as they land' },
          ]
        : [
            { id: 'revenue', label: 'Revenue', path: '/revenue', icon: DollarSign, description: 'Every creator’s gross, net and agency split' },
            { id: 'payroll', label: 'Staff Balances', path: '/payroll', icon: CreditCard, description: 'What each chatter and marketer has earned and is owed' },
            { id: 'tracking-links', label: 'Tracking Links', path: '/tracking-links', icon: Link2, description: 'Which Infloww link belongs to which marketer' },
            { id: 'sales', label: 'Live Sales', path: '/sales', icon: Zap, description: 'Every sale across every creator, as it lands' },
            { id: 'salary', label: 'Salary', path: '/salary', icon: BadgeDollarSign, description: 'What every staff member is paid, in one place' },
            { id: 'payouts', label: 'Payouts', path: '/payouts', icon: Wallet, description: 'Withdrawal requests and what has been paid' },
            { id: 'reports', label: 'Reports', path: '/reports', icon: BarChart3, description: 'Imports and analytics across the agency' },
          ]),
      // Where your own money goes. Staff on payroll and chatters on commission
      // are both paid through the withdrawal queue, so they set this themselves
      // (`/api/payroll/wallet` is scoped to the caller's own row).
      //
      // Not creators: they are paid on their revenue split and are not in the
      // withdrawal system at all (withdrawal_requests is keyed on chatter_id),
      // so the wallet was a control that could never do anything for them. The
      // route says no as well — see denyRole('creator') in routes/payroll.js.
      ...(isCreator(u) ? [] : [{ id: 'wallet', label: 'Wallet', path: '/payroll/wallet', icon: Landmark, description: 'Where your payouts are sent' }]),
    ],
    stat: (u) => (isChatter(u)
      ? {
          endpoint: '/api/dashboard/chatter',
          pick: (d) => `$${Math.round(d?.stats?.availableToWithdraw || 0).toLocaleString()}`,
          suffix: 'to withdraw',
        }
      : isMarketer(u)
      ? {
          endpoint: '/api/dashboard/marketer',
          pick: (d) => `$${Math.round(d?.stats?.availableToWithdraw || 0).toLocaleString()}`,
          suffix: 'to withdraw',
        }
      : isCreator(u)
      ? {
          // A creator has no access to the withdrawals queue below, so that stat
          // rendered as a permanent "not connected". Their own share this month is
          // the number they'd actually want on the card, and /api/revenue is
          // already pinned to their profile.
          endpoint: () => {
            const now = new Date();
            const first = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
            const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            return `/api/revenue?from=${first}&to=${today}`;
          },
          pick: (d) => {
            const t = d?.totals;
            if (!t || t.net == null) return null;
            return `$${Math.round(Math.max(0, t.net - (t.agencyProfit || 0))).toLocaleString()}`;
          },
          suffix: 'yours this month',
        }
      : {
          // Pending withdrawal requests (payouts awaiting action). manage_users-gated,
          // so non-admins get a clean "not connected" label rather than a bare dash.
          endpoint: '/api/withdrawals?status=pending',
          pick: (d) => (d?.requests || []).length,
          suffix: 'pending',
          fallback: 'not connected',
        }),
  },
  {
    id: 'operations',
    name: 'Operations',
    tagline: (u) => (isChatter(u) ? 'Your shifts & tasks'
      : isCreator(u) ? 'What needs doing'
      // Only admins, managers and chatters get Shifts in the nav below, so for
      // a marketer this room really is tasks, the plan and announcements.
      : isMarketer(u) ? 'Tasks, plan & announcements'
      : 'Tasks & shift scheduling'),
    icon: CheckSquare,
    accent: 'color-mix(in srgb, var(--brand-2) 62%, var(--brand-1))',
    // Chatters land on the shift schedule — the thing they open Operations for.
    landing: (u) => (isChatter(u) ? '/shifts' : '/tasks'),
    // Creators come in for Tasks alone: /api/tasks self-scopes to what is theirs
    // (assigned to them, or linked to their own creator profile), so the room
    // shows them their own work and nothing about the agency's.
    visible: (u) => isStaff(u) || isCreator(u),
    // "Operations" everywhere for a creator — the room card, the tab bar, the
    // sidebar and the page heading. Calling the same destination Tasks in one
    // place and Operations in another is the kind of small inconsistency that
    // makes an app feel assembled rather than designed.
    // The content plan is reachable from here as well as from Content. Planning
    // the week and working the week are the same job to the people doing it, and
    // making them remember which room a schedule lives in is the sort of thing
    // that gets a feature quietly abandoned. The id belongs to the Content room,
    // so opening it moves the sidebar there — it is a content page either way.
    nav: (u) => (isCreator(u)
      ? [
        { id: 'tasks', label: 'Operations', path: '/tasks', icon: CheckSquare },
        { id: 'content', label: 'Content Plan', path: '/content', icon: CalendarDays, description: 'What you have been asked to make this week' },
      ]
      : [
        { id: 'tasks', label: 'Tasks', path: '/tasks', icon: CheckSquare, description: 'Who is doing what, and by when' },
        { id: 'content-plan', label: 'Content Plan', path: '/content/plan', icon: CalendarDays, description: 'Build a creator’s week of ideas' },
        ...(['admin', 'manager', 'chatter'].includes(u?.role) ? [{ id: 'shifts', label: 'Shifts', path: '/shifts', icon: Calendar, description: 'The weekly rota and who is on it' }] : []),
        // Shown to whoever can register a phone, whoever has one assigned, and
        // marketers — the posting side of the agency is who actually picks a
        // phone up. A chatter with no phone still isn't given a link to an
        // empty page; `hasDevice` comes from /me.
        ...(canManageUsers(u) || isMarketer(u) || u?.hasDevice ? [{ id: 'remote-control', label: 'Remote Control', path: '/remote-control', icon: Smartphone, description: 'Drive an agency iPhone from the browser' }] : []),
        { id: 'announcements', label: 'Announcements', path: '/announcements', icon: Megaphone },
      ]),
    // The tasks endpoint self-scopes for non-managers, so this is "my open tasks"
    // for a chatter and "all open tasks" for staff.
    stat: { endpoint: '/api/tasks', pick: (d) => (d.tasks || []).filter((t) => t.status !== 'complete').length, suffix: 'open tasks' },
  },
  {
    // Its own room rather than a nav item inside Operations: the library has its
    // own department structure and is the thing staff open most often.
    id: 'sops',
    name: 'Academy',
    tagline: 'Training courses & playbooks',
    icon: GraduationCap,
    accent: 'color-mix(in srgb, var(--brand-2) 70%, var(--brand-1))',
    // Training first: it's the thing with a deadline attached. The library is
    // always one click away and keeps its own /sops URL.
    landing: () => '/academy',
    visible: isStaff,
    nav: () => [
      { id: 'academy', label: 'Training', path: '/academy', icon: GraduationCap, description: 'Courses, modules and quizzes' },
      { id: 'sops', label: 'SOP Library', path: '/sops', icon: BookOpen, description: 'Playbooks, by department' },
    ],
    stat: { endpoint: '/api/sop/stats', pick: (d) => d.totalSops, suffix: 'SOPs' },
  },
  {
    id: 'agents',
    name: 'AI Agents',
    tagline: 'Live automations',
    icon: Bot,
    accent: 'color-mix(in srgb, var(--brand-2) 80%, var(--brand-1))',
    landing: () => '/agents',
    visible: isInternalStaff,
    nav: () => [
      { id: 'agents', label: 'Agents', path: '/agents', icon: Bot },
    ],
    // Count of currently-running background jobs across the real automations.
    // AgentsPage owns the detail; this is the glance number.
    stat: { agentsRunning: true, live: true, suffix: 'running' },
  },
  {
    // Every role gets Messages — it's the one room that isn't about a job function.
    id: 'messages',
    name: 'Messages',
    tagline: 'Team chat & group threads',
    icon: MessageSquare,
    accent: 'color-mix(in srgb, var(--brand-2) 90%, var(--brand-1))',
    landing: () => '/messages',
    visible: (u) => !!u,
    nav: () => [
      { id: 'messages', label: 'Messages', path: '/messages', icon: MessageSquare },
    ],
    stat: { endpoint: '/api/messages/unread', pick: (d) => d.unread, suffix: 'unread' },
  },
  {
    // Every role gets this room, same reasoning as Messages — it isn't a job
    // function, it's the fact of being someone the agency has an agreement
    // with. What it shows is different for everyone in it: your own contract,
    // and — only for a creator — the invoices raised against you. The API
    // scopes every read to the caller; nothing here is a shared list.
    id: 'legal',
    name: 'Legal',
    tagline: (u) => (isCreator(u) ? 'Your contract & invoices' : 'Your contract'),
    icon: Scale,
    accent: 'color-mix(in srgb, var(--brand-2) 95%, var(--brand-1))',
    landing: () => '/legal/contract',
    visible: (u) => !!u,
    // Two tabs, not one page with two sections: Contract for everyone,
    // Invoices only where it means anything — an invoice is the agency
    // billing a creator, which has no equivalent for staff, so a chatter
    // never sees a second tab that would only ever say "nothing here".
    nav: (u) => [
      { id: 'legal-contract', label: 'Contract', path: '/legal/contract', icon: Scale, description: 'The agreement on file for you' },
      ...(isCreator(u) ? [
        { id: 'legal-invoices', label: 'Invoices', path: '/legal/invoices', icon: Receipt, description: 'Invoices raised for you, as PDFs' },
      ] : []),
      // A manager attaches these from the same room rather than from Team or
      // Creators — a contract or an invoice is not really "team admin" or
      // "creator admin", it is its own small piece of paperwork, and putting
      // it in the room everyone else already opens for theirs keeps staff from
      // needing to remember a second place it lives.
      ...(canManageUsers(u) ? [
        { id: 'legal-manage', label: 'Manage Documents', path: '/legal/manage', icon: FileSignature, description: 'Attach a contract or invoice to someone' },
      ] : []),
    ],
  },
  {
    id: 'admin',
    name: 'Admin',
    tagline: 'App configuration',
    icon: Settings,
    accent: 'color-mix(in srgb, var(--brand-2) 100%, var(--brand-1))',
    landing: () => '/settings',
    visible: canManageUsers,
    nav: () => [
      { id: 'settings', label: 'Settings', path: '/settings', icon: Settings },
    ],
    // Deliberately no stat: Settings has no single meaningful number.
  },
];

// ─── Mobile tab bar ─────────────────────────────────────────────────────────
// The creator app is opened on a phone, one-handed, so its five most-used
// destinations sit on a bottom bar instead of behind a hamburger → drawer →
// room-scoped sidebar. These are picked from what a creator actually does in a
// week, in that order; everything else (Finance, Notifications, Settings) is
// still one tap away in the drawer, which for creators lists every room at once
// rather than only the one they are standing in.
//
// Returns null for any role without a tab bar — staff keep the sidebar, which
// is the right shape for their much larger surface area.
const CREATOR_TABS = [
  { id: 'dashboard', label: 'Home', path: '/creator-dashboard', icon: LayoutDashboard },
  { id: 'content', label: 'Content', path: '/content', icon: Clapperboard },
  { id: 'tasks', label: 'Operations', path: '/tasks', icon: CheckSquare },
  { id: 'messages', label: 'Messages', path: '/messages', icon: MessageSquare },
];

// A chatter's week, in the order they open it: their numbers, the rota, the
// tasks they've been given, and their pay. Everything else — the playbooks, the
// wallet — is one tap away in the drawer, which for a role with a tab bar lists
// every room at once rather than only the one they are standing in.
const CHATTER_TABS = [
  { id: 'dashboard', label: 'Home', path: '/dashboard', icon: LayoutDashboard },
  { id: 'shifts', label: 'Shifts', path: '/shifts', icon: Calendar },
  { id: 'my-earnings', label: 'Earnings', path: '/payroll/my-earnings', icon: Wallet },
  { id: 'messages', label: 'Messages', path: '/messages', icon: MessageSquare },
];

// A marketer's is the content pipeline plus their own numbers: the dashboard
// (subs, commission, balance), the vault they file into, the queue they publish
// from, and chat.
const MARKETER_TABS = [
  { id: 'dashboard', label: 'Home', path: '/dashboard', icon: LayoutDashboard },
  { id: 'content', label: 'Content', path: '/content', icon: Clapperboard },
  { id: 'posting-library', label: 'Posting', path: '/posting-library', icon: Send },
  { id: 'messages', label: 'Messages', path: '/messages', icon: MessageSquare },
];

/**
 * Where a role lands after signing in.
 *
 * One place, because it was two: the login page sent everybody to /dashboard
 * while a returning session hitting / got the Rooms hub, so a creator's home
 * depended on how they arrived. Creators now land on Rooms either way — it is
 * the map of their app, and the dashboard is one room on it rather than the
 * front door.
 *
 * Staff keep /dashboard. Their hub is one click from every page and the agency
 * dashboard is genuinely the first thing they open; this change is deliberately
 * scoped to the creator app.
 */
export function landingRoute(user) {
  return isCreator(user) ? '/' : '/dashboard';
}

/**
 * The five destinations this role gets on a phone, or null for a role that
 * keeps the sidebar.
 *
 * Admins and managers keep the sidebar: their surface area is far too large for
 * five tabs to be an honest summary of it, and picking five would mean hiding
 * the rest behind a bar that claims to be the whole map. Creators, chatters and
 * marketers each do a small, well-defined set of things every day, which is
 * exactly what a tab bar is for.
 */
// Admins and managers run the whole agency, so there is no small set of daily
// destinations to pick — Rooms is the way in, and the four alongside it are the
// ones every staff day actually touches. Previously this role had no tab bar at
// all, which on a phone left the hub reachable only through the hamburger.
const STAFF_TABS = [
  { id: 'dashboard', label: 'Home', path: '/dashboard', icon: LayoutDashboard },
  { id: 'creators', label: 'Creators', path: '/creators', icon: Users, description: 'The roster, and their Infloww connections' },
  { id: 'content', label: 'Content', path: '/content', icon: Clapperboard },
  { id: 'messages', label: 'Messages', path: '/messages', icon: MessageSquare },
];

/**
 * The bottom bar, for every role.
 *
 * It used to return null for staff, so an admin on a phone had a tab bar's worth
 * of empty space and a hamburger. Now the bar is the constant: whatever the
 * role, Rooms is the first tab and the way back to everything.
 */
export function mobileTabs(user) {
  if (!user) return null;
  if (isCreator(user)) return CREATOR_TABS;
  if (isChatter(user)) return CHATTER_TABS;
  if (isMarketer(user)) return MARKETER_TABS;
  return STAFF_TABS;
}

/**
 * Every destination the user has, grouped by room.
 *
 * The sidebar normally shows one room's nav, which is right on a desktop where
 * the hub is always a click away in the corner. In a mobile drawer it strands
 * you: to get from Tasks to your earnings you have to close the drawer, go to
 * Rooms, pick Finance, then pick the page. This is the whole map instead.
 */
export function fullNavMap(user) {
  return visibleModules(user)
    .map((m) => ({ module: m, items: moduleNav(m, user) }))
    .filter((g) => g.items.length > 0);
}

export function visibleModules(user) {
  return MODULES.filter((m) => {
    try { return m.visible(user); } catch { return false; }
  });
}

// `tagline` and `stat` may be plain values or functions of the user, since what a
// room *is* differs by role (Finance is "revenue & payroll" to staff and "your
// earnings" to a chatter). These resolve either form.
export function moduleTagline(module, user) {
  return typeof module?.tagline === 'function' ? module.tagline(user) : module?.tagline;
}

export function moduleStat(module, user) {
  return typeof module?.stat === 'function' ? module.stat(user) : module?.stat;
}

export function moduleNav(module, user) {
  if (!module) return [];
  return typeof module.nav === 'function' ? module.nav(user) : module.nav;
}

export function getModule(id) {
  return MODULES.find((m) => m.id === id) || null;
}

// Which room does a given page (currentPage id) belong to?
export function getModuleForPage(currentPage, user) {
  if (!currentPage) return null;
  // A room's own landing page (RoomPage) names itself "room:<id>" — it belongs
  // to that room by definition rather than by containing one of its nav items.
  if (currentPage.startsWith('room:')) {
    const m = MODULES.find((mod) => mod.id === currentPage.slice(5));
    if (!m) return null;
    try { return m.visible(user) ? m : null; } catch { return null; }
  }
  for (const m of MODULES) {
    // A room the user can't see never claims the page — otherwise a chatter on
    // /shifts would inherit a room they aren't allowed in.
    try { if (!m.visible(user)) continue; } catch { continue; }
    const items = moduleNav(m, user);
    if (items.some((it) => it.id === currentPage)) return m;
  }
  return null;
}
